import {
  DEFAULT_AVATAR_LOOK,
  HAIR_STYLE_OPTIONS,
  HAIR_SWATCHES as HAIR_SWATCHES_SHARED,
  normalizeAvatarLook,
  SHIRT_SWATCHES as SHIRT_SWATCHES_SHARED,
  SKIN_SWATCHES as SKIN_SWATCHES_SHARED,
} from "@/lib/office/avatar-look";
import { OFFICE_UNLOCKS } from "@/lib/office/unlocks";
import type { AvatarColors } from "./world-engine";

export const STORAGE_KEY = "nymbl_vyllage_v1";
/** One-time flag: localStorage blob was pushed to the server profile. */
export const MIGRATED_KEY = "nymbl_vyllage_profile_migrated_v1";

export const DEFAULT_COLORS: AvatarColors = { ...DEFAULT_AVATAR_LOOK };

export const SHIRT_SWATCHES = SHIRT_SWATCHES_SHARED;
export const HAIR_SWATCHES = HAIR_SWATCHES_SHARED;
export const SKIN_SWATCHES = SKIN_SWATCHES_SHARED;

/** @deprecated Prefer HAIR_STYLE_OPTIONS from avatar-look */
export const STYLE_OPTIONS = HAIR_STYLE_OPTIONS;

export const STATUSES = [
  { id: "available", label: "Available", color: "#2EA86A" },
  { id: "focus", label: "Focus", color: "#64479D" },
  { id: "meeting", label: "In meeting", color: "#1E6CC7" },
  { id: "away", label: "Away", color: "#8894A8" },
] as const;

export type StatusId = (typeof STATUSES)[number]["id"];

/**
 * Canonical tester-facing spatial A/V tip.
 * Keep one string so onboarding, join toast, and sys feed stay in lockstep.
 * Focus copy splits soft audio vs hidden cam (video is not "muffled").
 */
export const SPATIAL_AV_TIP =
  "Near teammates for voice & video on the open floor · Walk into Company Meeting or a Huddle to hear everyone in that room · Personal offices stay private · Focus softens sound and hides your camera";

/** @deprecated Prefer OFFICE_UNLOCKS from @/lib/office/unlocks — re-exported for office UI. */
export const UNLOCKS = OFFICE_UNLOCKS;
export type UnlockId = (typeof OFFICE_UNLOCKS)[number]["id"];

export type PersistedVyllage = {
  name: string;
  colors: AvatarColors;
  points: number;
  streak: number;
  lastDay: string | null;
  unlockedIds: string[];
};

export type SideTab = "chat" | "people" | "perks";

export type FeedItem =
  | { id: string; kind: "sys"; text: string }
  | {
      id: string;
      kind: "msg";
      from: string;
      color: string;
      text: string;
      zone: string;
      vol: number;
    };

export type ToastItem = { id: number; text: string };

export function loadPersisted(): PersistedVyllage | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const p = parsed as Partial<PersistedVyllage>;
    return {
      name: typeof p.name === "string" ? p.name : "You",
      colors: normalizeAvatarLook(p.colors ?? DEFAULT_COLORS),
      points: typeof p.points === "number" ? p.points : 0,
      streak: typeof p.streak === "number" ? p.streak : 5,
      lastDay: typeof p.lastDay === "string" ? p.lastDay : null,
      unlockedIds: Array.isArray(p.unlockedIds)
        ? p.unlockedIds.filter((x): x is string => typeof x === "string")
        : [],
    };
  } catch {
    return null;
  }
}

export function savePersisted(data: PersistedVyllage): void {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        ...data,
        colors: normalizeAvatarLook(data.colors),
      }),
    );
  } catch {
    // ignore quota / private mode
  }
}

export function initialsOf(name: string): string {
  return name
    .split(" ")
    .map((x) => x[0] ?? "")
    .join("")
    .slice(0, 2)
    .toUpperCase();
}
